import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FilePlus2, Loader2, ArrowRight } from 'lucide-react';
import PatientPortalShell from '../components/PatientPortalShell';
import ReferralStatusBadge from '../components/ReferralStatusBadge';
import { referralPortalApi } from '../api/referralPortal.api';

export default function PatientReferralPortalPage() {
  const [me, setMe] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([referralPortalApi.me(), referralPortalApi.myRequests()])
      .then(([m, r]) => { setMe(m); setRows(r || []); })
      .finally(() => setLoading(false));
  }, []);

  const branding = me?.tenant?.branding || {};
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#F8F4F0]">
        <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <PatientPortalShell branding={branding} tenantName={me?.tenant?.name}>
      <section className="mb-8 overflow-hidden rounded-3xl border border-orange-100 bg-white shadow-sm">
        <div className="grid gap-0 md:grid-cols-[1.2fr_0.8fr]">
          <div className="p-6 sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-700/80">Referral desk</p>
            <h2 className="mt-2 font-heading text-3xl text-stone-900">Request a referral letter</h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-stone-600">
              Ask your campus doctor for an official referral letter to visit a hospital or specialist clinic.
              Track FIC approval for out-station requests and download the issued PDF when ready.
            </p>
            <Link
              to="/patient/referrals/new"
              className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow"
              style={{ background: branding.primary_color || '#F15A22' }}
            >
              <FilePlus2 className="h-4 w-4" /> Start new request
            </Link>
          </div>
          <div
            className="relative min-h-[180px] p-6 text-white sm:p-8"
            style={{ background: `linear-gradient(160deg, ${branding.primary_color || '#F15A22'}, #7c2d12)` }}
          >
            {branding.logo_url && (
              <img src={branding.logo_url} alt="" className="mb-4 h-16 w-16 rounded-full bg-white object-contain p-1" />
            )}
            <p className="text-lg font-semibold">{me?.tenant?.name}</p>
            <p className="mt-1 text-sm text-orange-50/90">{branding.tagline}</p>
            <p className="mt-4 text-xs text-orange-100/80">{branding.address}</p>
          </div>
        </div>
      </section>

      <div className="mb-3 flex items-end justify-between">
        <h3 className="font-heading text-2xl text-stone-900">My referral requests</h3>
        <span className="text-sm text-stone-500">{rows.length} total</span>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-stone-300 bg-white/70 px-6 py-16 text-center">
          <p className="font-medium text-stone-700">No referral requests yet</p>
          <p className="mt-1 text-sm text-stone-500">Submit your first request to get a doctor-issued letter.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.id}>
              <Link
                to={`/patient/referrals/${r.id}`}
                className="group flex items-center justify-between gap-4 rounded-2xl border border-stone-200 bg-white px-4 py-4 shadow-sm transition hover:border-orange-300 hover:shadow"
              >
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-stone-900">{r.request_number}</p>
                    <ReferralStatusBadge status={r.status} />
                    {r.station_type === 'out_station' && (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                        Out-station
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-stone-600">
                    {r.seeking?.hospital_clinic} · {r.seeking?.specialisation}
                  </p>
                  <p className="text-xs text-stone-400">
                    For {r.patient?.name} ({r.patient?.relationship})
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-stone-400 transition group-hover:text-orange-600" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </PatientPortalShell>
  );
}
